import { defineStore } from "pinia";
import { useOidcStore } from "vue3-oidc";
import db from "@/db";
import { userRestClient, UserManagementClient } from "@/api";
import { useAgenciesStore } from "@/store/modules/agencies";
import { useProjectsStore } from "@/store/modules/projects";

const LAST_USER_EMAIL_KEY = "atmo-last-user-email";

export const useUsersStore = defineStore("users", {
  state: () => ({
    currentUser: null,
  }),
  getters: {
    currentUserEmail: (state) => state.currentUser?.email,
    currentUserUuid: (state) => state.currentUser?.uuid,
    isCurrentUserDeveloper: (state) => state.currentUser?.isDev(),
    isCurrentUserProjectManager: (state) =>
      useProjectsStore().currentProjectManagerEmail ===
      state.currentUser?.email,
    // INFO - L.L - 14/09/2022 - TODO match with user groups and role
    isCurrentUserSupervisor: () => true,
    isCurrentUserProjectManagerOrDeveloper() {
      return this.isCurrentUserProjectManager || this.isCurrentUserDeveloper;
    },
    isCurrentUserFromMeasurementAgency: (state) =>
      !!state.currentUser?.isFromMeasurementAgency,
  },
  actions: {
    async fetchCurrentUser() {
      if (!window.navigator.onLine) return;

      const email = useOidcStore().state.value.user?.profile?.email;
      if (!email) return;

      const userResponse = await userRestClient.retrieveByEmail({ email });
      if (!userResponse) return;

      const dexieUuid = await db.models.User.createOrUpdate(userResponse);

      const isFromMeasurementAgency =
        await this.fetchIsFromMeasurementAgency(userResponse);
      await db.users.update(dexieUuid, {
        lastOnlineAuthAt: new Date(),
        ...(isFromMeasurementAgency !== null && { isFromMeasurementAgency }),
      });
      const user = await db.users.get({ uuid: dexieUuid });

      this.currentUser = user;
      localStorage.setItem(LAST_USER_EMAIL_KEY, user.email);

      return user;
    },

    async fetchIsFromMeasurementAgency(user) {
      try {
        const agency = await useAgenciesStore().fetchAgencyByUserUuid(
          user.usermanagementUuid
        );
        if (!agency?.code) return false;

        return !!(await userRestClient.isFromMeasurementAgency({
          agencyCode: agency.code,
        }));
      } catch (error) {
        console.error("Could not resolve the measurement agency flag", error);
        return null;
      }
    },

    async initializeCurrentUser() {
      await this.fetchCurrentUser();
    },

    async getCachedCurrentUser() {
      const email =
        useOidcStore().state.value.user?.profile?.email ??
        localStorage.getItem(LAST_USER_EMAIL_KEY);
      return email ? db.users.get({ email }) : undefined;
    },

    async restoreCachedCurrentUser() {
      if (this.currentUser) return;
      const user = await this.getCachedCurrentUser();
      if (user) this.currentUser = user;
    },

    async searchUsers(filters) {
      const { results } = await UserManagementClient.list({ filters });

      return results;
    },
  },
});
